import { useEffect, useState } from 'react';
import type { PromotionAssetViewModel, TranslationKey } from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared';
import { safePromotionAssetImageUrl } from '../lib/promotion-kit-downloads';

export function PromotionStoryPreview(props: {
  readonly asset: PromotionAssetViewModel | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const imageUrl = safePromotionAssetImageUrl(props.asset?.imageUrl ?? null);
  const [imageFailed, setImageFailed] = useState(false);
  const shouldRenderImage = imageUrl !== null && !imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  return (
    <section className="border-border bg-card shadow-admin-card rounded-lg border p-4">
      <h2 className="font-display text-foreground text-xl">
        {t('admin.promotionKit.preview.storyTitle' as TranslationKey)}
      </h2>
      <div className="bg-zaatar-deep text-paper dark:bg-sand-900 dark:text-sand-50 relative mx-auto mt-4 flex aspect-[9/16] max-h-[520px] max-w-[292px] flex-col justify-between overflow-hidden rounded-xl p-6 text-center">
        {shouldRenderImage ? (
          <img
            src={imageUrl}
            alt=""
            className="absolute inset-0 size-full object-cover"
            crossOrigin="anonymous"
            data-testid="promotion-story-image"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div
            className="bg-zaatar-deep dark:bg-sand-900 absolute inset-0 grid place-items-center"
            data-testid="promotion-story-image-fallback"
          >
            <span className="border-paper/20 text-paper rounded-full border px-5 py-3 text-lg font-bold">
              Dukanti
            </span>
          </div>
        )}
        <div className="from-zaatar-deep/90 via-zaatar-deep/55 dark:from-sand-950/90 dark:via-sand-950/60 absolute inset-0 bg-gradient-to-t to-transparent" />
        <div className="relative">
          <p className="text-sm font-medium">{props.asset?.storeName ?? 'Dukanti'}</p>
          <h3 className="mt-5 text-3xl font-bold">{props.asset?.targetTitle ?? ''}</h3>
          {props.asset?.targetSubtitle ? (
            <p className="text-paper/85 dark:text-sand-100 mt-3 line-clamp-4 text-sm">
              {props.asset.targetSubtitle}
            </p>
          ) : null}
        </div>
        <p className="bg-paper/15 dark:bg-sand-50/10 relative rounded-full px-4 py-2 text-sm font-semibold">
          {props.asset ? t(props.asset.ctaKey) : t('promotionKit.story.cta' as TranslationKey)}
        </p>
      </div>
    </section>
  );
}
